// src/contexts/SalesContext.jsx (CON LOGS INTELIGENTES)
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { generateEpochUniqueId } from "../scripts/generalScript";
import { useAuth } from "./AuthContext";
import { useOrders } from "../hooks/useOrders";
import { DELIVERY_ZONES } from "../config/delivery";
import toast from "react-hot-toast";

export const SalesContext = createContext();

export const useSalesContext = () => useContext(SalesContext);

export const SalesProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { createOrder, isCreatingOrder } = useOrders();
  // En SalesContext.jsx, dentro de SalesProvider
  const [refillItems, setRefillItems] = useState([]);
  const [refillJugsData, setRefillJugsData] = useState([]);

  // En SalesContext.jsx, dentro de SalesProvider
  const startRefillProcess = useCallback((items, jugs) => {
    // console.log("📝 SalesContext: Iniciando proceso de recambio", {
    //   itemCount: items.length,
    // });
    setRefillItems(items);
    setRefillJugsData(jugs);
  }, []);

  const valorSuscripcion = process.env.REACT_APP_REFILL_PRICE;

  // 📊 CONTADOR DE RENDERS PARA DEBUGGING
  const renderCountRef = useRef(0);
  const lastRenderTimeRef = useRef(Date.now());

  renderCountRef.current++;
  const currentTime = Date.now();
  const timeSinceLastRender = currentTime - lastRenderTimeRef.current;

  // ⚠️ Solo mostrar warning si hay muchos re-renders en poco tiempo
  if (renderCountRef.current > 1 && timeSinceLastRender < 100) {
    // console.warn(
    //   `🔥 SalesProvider: ${renderCountRef.current} renders en ${timeSinceLastRender}ms`
    // );
  } else if (renderCountRef.current % 10 === 0) {
    // console.log(`📊 SalesProvider: ${renderCountRef.current} renders totales`);
  }

  lastRenderTimeRef.current = currentTime;

  // Estados principales
  const [selectedSugs, setSelectedSugs] = useState(0);
  const [selectedDispensers, setSelectedDispensers] = useState(0);
  const [isValid, setIsValid] = useState(null);

  // Estados de usuario
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [userId, setUserId] = useState("");

  // Estados de dirección
  const [selectedAddress, setSelectedAddress] = useState("");
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [addressDetails, setAddressDetails] = useState("");
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [newOrderTotal, setNewOrderTotal] = useState(0);
  const [addressHasDetails, setAddressHasDetails] = useState(true);
  const [newSalesTerms, setNewSalesTerms] = useState(false);
  const [selectedCoordinates, setSelectedCoordinates] = useState(null);

  // Estados de progreso y resultado
  const [progress, setProgress] = useState(0);
  const [shortId, setShortId] = useState(() => generateEpochUniqueId());
  const [isDataComplete, setIsDataComplete] = useState(false);
  const [newOrderData, setNewOrderData] = useState(null);
  const [imageAddress, setImageAddress] = useState(null);
  const [isCoordsLoading, setIsCoordsLoading] = useState(false);
  const [selectedZone, setSelectedZone] = useState(null);
  const [total, setTotal] = useState(0);

  const [isChangingAddress, setIsChangingAddress] = useState(false);
  // Control de ejecución de generación de leads/órdenes
  const [shouldRunNewShopLeadGeneration, setShouldRunNewShopLeadGeneration] =
    useState(false);
  const hasRunNewShopLeadGeneration = useRef(false);

  // Referencias estables para valores que no cambian frecuentemente
  const userDataRef = useRef();
  const contextValueRef = useRef();

  // Memoizar datos de usuario para evitar updates innecesarios
  const userData = useMemo(() => {
    if (user?.userData) {
      const newUserData = {
        name: `${user.userData.first_name || ""} ${
          user.userData.last_name || ""
        }`.trim(),
        phone: user.userData.phone || "",
        email: user.userData.email || "",
        userId: user.id || "",
      };

      // Solo actualizar si realmente cambió
      if (JSON.stringify(userDataRef.current) !== JSON.stringify(newUserData)) {
        // console.log("📝 SalesProvider: Datos de usuario actualizados");
        userDataRef.current = newUserData;
        return newUserData;
      }
      return userDataRef.current || newUserData;
    }
    return (
      userDataRef.current || { name: "", phone: "", email: "", userId: "" }
    );
  }, [
    user?.userData?.first_name,
    user?.userData?.last_name,
    user?.userData?.phone,
    user?.userData?.email,
    user?.id,
  ]);

  // Efecto optimizado para actualizar datos de usuario
  useEffect(() => {
    if (userData) {
      setName((prev) => (prev !== userData.name ? userData.name : prev));
      setPhone((prev) => (prev !== userData.phone ? userData.phone : prev));
      setEmail((prev) => (prev !== userData.email ? userData.email : prev));
      setUserId((prev) => (prev !== userData.userId ? userData.userId : prev));
    }
  }, [userData]);

  // Funciones estables (no dependen de estado)
  const staticFunctions = useMemo(() => {
    // console.log(
    //   "🔧 SalesProvider: Recreando funciones estáticas (solo debería pasar una vez)"
    // );

    const resetData = () => {
      setSelectedSugs(0);
      setSelectedDispensers(0);
      setName("");
      setPhone("+57 ");
      setEmail("");
      setSelectedAddress("");
      setSelectedAddressId("");
      setAddressDetails("");
      setAddressHasDetails(true);
      setNewSalesTerms(false);
      setSelectedCoordinates(null);
      setSelectedDeliveryDate(null);
      setSelectedTimeSlot(null);
      setDeliveryFee(0);
      setImageAddress(null);
      setIsCoordsLoading(false);
      setNewOrderData(null);

      // Reset de control de ejecución
      hasRunNewShopLeadGeneration.current = false;
      setShouldRunNewShopLeadGeneration(false);
      setIsDataComplete(false);
      setProgress(0);
      setShortId(generateEpochUniqueId());
    };

    const triggerNewShopLeadGeneration = () => {
      setShouldRunNewShopLeadGeneration(true);
    };

    const resetNewShopLeadGenerationFlag = () => {
      hasRunNewShopLeadGeneration.current = false;
    };

    const nextStepBreak = (duration) => {
      return new Promise((resolve) => setTimeout(resolve, duration));
    };

    return {
      resetData,
      triggerNewShopLeadGeneration,
      resetNewShopLeadGenerationFlag,
      nextStepBreak,
    };
  }, []); // Sin dependencias - funciones estables

  // Efecto optimizado para validar si los datos están completos
  const checkDataComplete = useCallback(() => {
    const isComplete =
      name !== "" &&
      phone !== "+57 " &&
      email !== "" &&
      selectedAddress !== "" &&
      selectedCoordinates !== null &&
      newSalesTerms === true;

    setIsDataComplete((prev) => (prev !== isComplete ? isComplete : prev));
  }, [name, phone, email, selectedAddress, selectedCoordinates, newSalesTerms]);

  useEffect(() => {
    checkDataComplete();
  }, [checkDataComplete]);

  useEffect(() => {
    // AGREGAR: No recalcular durante cambios de dirección
    if (isChangingAddress) {
      return;
    }

    // Solo calcular si tenemos valores válidos
    if (
      selectedSugs !== null &&
      selectedSugs !== undefined &&
      deliveryFee !== null &&
      deliveryFee !== undefined
    ) {
      const totalDeliveryFee = deliveryFee * selectedSugs;
      const newTotal = selectedSugs * valorSuscripcion + totalDeliveryFee;

      setNewOrderTotal((prevTotal) => {
        if (Math.abs(prevTotal - newTotal) > 0.01) {
          return newTotal;
        }
        return prevTotal;
      });
    }
  }, [selectedSugs, deliveryFee, valorSuscripcion, isChangingAddress]); // AGREGAR isChangingAddress

  const calculateDeliveryFee = useCallback((zone) => {
    return DELIVERY_ZONES[`ZONE_${zone}`]?.price || 0;
  }, []);

  // FUNCIÓN PRINCIPAL: Crear orden real (memoizada)

  const createRealOrder = useCallback(async () => {
    console.log("🚀 SalesProvider: Iniciando createRealOrder");

    try {
      // Preparar datos según el tipo de orden
      let orderData;
      let orderType;

      // Detectar si es refill o new items
      if (refillItems && refillItems.length > 0) {
        // Es un pedido de refills
        orderType = "refill";

        // Agrupar refills por dirección (igual que en useNewRefillsLogic)
        const groupedByAddress = refillItems.reduce((acc, item) => {
          const existing = acc.find(
            (group) => group.addressId === item.addressId
          );
          if (existing) {
            existing.items.push(item);
          } else {
            acc.push({
              addressId: item.addressId,
              address: item.address,
              city: item.city,
              latitude: item.latitude,
              longitude: item.longitude,
              items: [item],
            });
          }
          return acc;
        }, []);

        // Calcular subtotal de refills
        const refillSubtotal = refillItems.length * 18000; // Precio fijo por refill

        orderData = {
          items: refillItems,
          groupedByAddress: groupedByAddress,
          scheduledDate: selectedDeliveryDate,
          timeSlot: selectedTimeSlot,
          totals: {
            subtotal: refillSubtotal,
            deliveryFee: deliveryFee,
            total: newOrderTotal,
          },
        };
      } else {
        // Es un pedido de new items
        orderType = "new_items";

        // Calcular subtotal de productos nuevos
        // Asumiendo precios fijos (deberías obtenerlos del inventario)
        const jugPrice = 45000;
        const dispenserPrice = 75000;
        const itemsSubtotal =
          selectedSugs * jugPrice + selectedDispensers * dispenserPrice;

        orderData = {
          selectedProducts: {
            jugs: selectedSugs,
            dispensers: selectedDispensers,
          },
          selectedAddress,
          selectedAddressId,
          selectedCoordinates,
          addressDetails,
          selectedDeliveryDate,
          selectedTimeSlot,
          totals: {
            subtotal: itemsSubtotal,
            deliveryFee,
            total: newOrderTotal,
          },
        };
      }

      // Navegar a la pantalla de procesamiento con los datos
      navigate("/procesando-pedido", {
        state: {
          orderType,
          orderData: {
            ...orderData,
            // Datos adicionales del usuario
            userData: {
              name,
              phone,
              email,
              userId,
            },
            termsAccepted: newSalesTerms,
          },
        },
      });
    } catch (error) {
      console.error("❌ Error preparando orden:", error);
      toast.error("Error al preparar tu pedido", {
        duration: 4000,
        position: "top-center",
      });
      setProgress(2);
    }
  }, [
    refillItems,
    selectedSugs,
    selectedDispensers,
    selectedAddress,
    selectedCoordinates,
    selectedDeliveryDate,
    selectedTimeSlot,
    addressDetails,
    deliveryFee,
    newOrderTotal,
    newSalesTerms,
    name,
    phone,
    email,
    userId,
    navigate,
  ]);

  // Efecto para ejecutar la generación de órdenes (optimizado)
  useEffect(() => {
    if (
      shouldRunNewShopLeadGeneration === true &&
      !hasRunNewShopLeadGeneration.current
    ) {
      createRealOrder();
      setShouldRunNewShopLeadGeneration(false);
      hasRunNewShopLeadGeneration.current = true;
    }
  }, [shouldRunNewShopLeadGeneration, createRealOrder]);

  // Valores dinámicos memoizados
  const dynamicValues = useMemo(() => {
    // console.log("🔄 SalesProvider: Recalculando dynamicValues");

    return {
      // Estados de productos
      selectedSugs,
      selectedDispensers,

      // Estados de usuario
      name,
      phone,
      email,
      userId,

      // Estados de dirección
      selectedAddress,
      selectedAddressId,
      addressDetails,
      addressHasDetails,
      selectedCoordinates,
      imageAddress,
      isValid,
      isCoordsLoading,
      calculateDeliveryFee,

      // Estados de entrega
      selectedDeliveryDate,
      selectedTimeSlot,
      deliveryFee,

      // Estados de orden
      newOrderTotal,
      newSalesTerms,
      newOrderData,
      shortId,

      selectedZone,
      setSelectedZone,
      // Estados de progreso
      progress,
      isDataComplete,
      isCreatingOrder,

      // Setters
      setSelectedSugs,
      setSelectedDispensers,
      setName,
      setPhone,
      setEmail,
      setUserId,
      setSelectedAddress,
      setSelectedAddressId,
      setAddressDetails,
      setAddressHasDetails,
      setSelectedCoordinates,
      setImageAddress,
      setIsValid,
      setIsCoordsLoading,
      setSelectedDeliveryDate,
      setSelectedTimeSlot,
      setDeliveryFee,
      setNewSalesTerms,
      setNewOrderData,
      setShortId,
      setProgress,
      setShouldRunNewShopLeadGeneration,
      refillItems,
      refillJugsData,
      startRefillProcess,
      total,
      setTotal,
    };
  }, [
    selectedSugs,
    selectedDispensers,
    name,
    phone,
    email,
    userId,
    selectedAddress,
    selectedAddressId,
    addressDetails,
    addressHasDetails,
    selectedCoordinates,
    imageAddress,
    isValid,
    isCoordsLoading,
    selectedDeliveryDate,
    selectedTimeSlot,
    deliveryFee,
    newOrderTotal,
    newSalesTerms,
    newOrderData,
    shortId,
    progress,
    isDataComplete,
    isCreatingOrder,
    isChangingAddress,
    setIsChangingAddress,
  ]);

  // Memoizar el valor del contexto combinando funciones estables y valores dinámicos
  const contextValue = useMemo(() => {
    // 🔍 Verificar si el contexto realmente cambió
    const newContextValue = {
      ...staticFunctions,
      ...dynamicValues,
    };

    const contextChanged =
      JSON.stringify(contextValueRef.current) !==
      JSON.stringify(newContextValue);

    if (contextChanged) {
      // console.log("📦 SalesProvider: Context value cambió, actualizando...");
      contextValueRef.current = newContextValue;
    } else {
      // console.log("✅ SalesProvider: Context value no cambió, usando cached");
      return contextValueRef.current || newContextValue;
    }

    return newContextValue;
  }, [staticFunctions, dynamicValues]);

  return (
    <SalesContext.Provider value={contextValue}>
      {children}
    </SalesContext.Provider>
  );
};
